import { createContext, FC, useCallback, useContext, useState } from 'react';
import { Icons, Showstopper, ShowstopperProps } from '@silverfort/sf-components';

export type OpenShowstopperParams = Omit<ShowstopperProps, 'open' | 'onClose'> & {
    onClose?: () => void;
};

interface ShowstopperContextValue {
    openShowstopper: (showstopperProps: OpenShowstopperParams) => void;
}

const DEFAULT_SHOWSTOPPER_PROPS: ShowstopperProps = {
    open: false,
    title: '',
    description: '',
    detailsItems: [],
    icon: Icons.IdentitySearch,
    onClose: () => {},
    onGetAccessClick: () => {}
};

const ShowstopperContext = createContext<ShowstopperContextValue | undefined>(undefined);

interface ShowstopperProviderProps {
    children: React.ReactNode;
}
export const ShowstopperProvider: FC<ShowstopperProviderProps> = ({ children }) => {
    const [showstopperProps, setShowstopperProps] = useState<ShowstopperProps>(DEFAULT_SHOWSTOPPER_PROPS);

    const openShowstopper: ShowstopperContextValue['openShowstopper'] = useCallback((props) => {
        const { onClose, ...restOfProps } = props;

        const handleClose = () => {
            setShowstopperProps((prevProps) => ({
                ...prevProps,
                open: false
            }));

            props.onClose?.();
        };

        setShowstopperProps({
            ...restOfProps,
            onClose: handleClose,
            open: true
        });
    }, []);

    const contextValue: ShowstopperContextValue = {
        openShowstopper
    };

    return (
        <ShowstopperContext.Provider value={contextValue}>
            <div className="relative w-full h-full">
                {children}
                <Showstopper {...showstopperProps} />
            </div>
        </ShowstopperContext.Provider>
    );
};

export const useShowstopper = () => {
    const context = useContext(ShowstopperContext);

    if (!context) {
        throw new Error('useShowstopper must be used within a ShowstopperProvider');
    }

    return context;
};
